import { useMutation } from '@apollo/client'
import { Icon, Loader, useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type { SelectMultipleOnSelectAll } from '@/components_deprecated'
import { Checkbox, Input } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import React, { useMemo, useRef } from 'react'
import { isMobile, isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import type { FileAttachment } from '@/utils/hooks'
import { useCompany, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { PERMISSIONS } from '@/utils/permissions'
import { SelectMultipleLeaderGroups } from '../../../components_deprecated/Select/SelectMultipleLeaderGroups'
import { SelectMultipleUsers } from '../../../components_deprecated/Select/SelectMultipleUsers'
import { Field } from '../../companies'
import type { ForumQuillEditorRef } from '../components'
import { ForumQuillEditor } from '../components'
import { CREATE_THREAD_MUTATION } from '../mutations'
import { FORUM_THREADS_QUERY } from '../queries'
import type { CreateThreadForm } from '../types'
import type {
  CreateThreadMutation,
  CreateThreadMutationVariables,
} from '../types.graphql'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  header h1 {
    display: flex;

    div.loader {
      margin-left: 1rem;
    }
  }
`

const UserSelectField = styled(Field)`
  display: flex;
  flex-direction: column;
  width: clamp(600px, 60vw, 100%);
  gap: 1rem;
  margin-bottom: 1rem;
  max-width: 100%;
`

const SelectorsRow = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;

  ${props => props.theme.media.mobile} {
    flex-direction: column;
  }
`

const SelectMultipleUsersWrapper = styled.div`
  position: relative;
  min-height: 54px;
  flex: 1;
  width: 100%;
`

const Form = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'title   choices'
    'content content';

  width: clamp(600px, 60vw, 100%);

  padding: 0;

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;

    width: 100%;
  }
`

interface SectionProps {
  area: string
}

const Section = styled.div<SectionProps>`
  grid-area: ${props => props.area};
`
const TitleSection = styled(Section).attrs({
  area: 'title',
})`
  display: flex;
  padding: 1rem 1rem 0;

  div.sticky {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 0 0 1rem;

    border-radius: ${props => props.theme.sizes.defaultBorderRadius} 0 0
      ${props => props.theme.sizes.defaultBorderRadius};
    border: 1px solid ${props => props.theme.colors.gray9};
    border-width: 1px 0 1px 1px;
    background-color: ${props => props.theme.colors.quaternary};

    transition: border-color 0.1s linear;
  }

  &:focus-within div.sticky {
    border-color: ${props => props.theme.colors.secondary};
  }
`
const TitleInput = styled(Input).attrs({
  fullWidth: true,
})`
  input {
    border-width: 1px 1px 1px 0;
    border-radius: 0 ${props => props.theme.sizes.defaultBorderRadius}
      ${props => props.theme.sizes.defaultBorderRadius} 0;
  }
`
const ChoicesSection = styled(Section).attrs({
  area: 'choices',
})`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-end;

  padding: 1rem 1rem 0 0;

  label {
    font-size: 0.9rem;
  }

  ${props => props.theme.media.mobile} {
    align-items: flex-start;
    padding: 1rem;

    border-bottom: 1px solid ${props => props.theme.colors.gray9};

    & > div + div {
      margin-top: 0.5rem;
    }
  }
`
const ContentSection = styled(Section).attrs({
  area: 'content',
})`
  .ql-editor {
    padding: 0.5rem !important;

    &::before {
      left: 0.5rem !important;
    }
  }
`

interface CreateThreadProps {
  isDirectMessage?: boolean
}

export const CreateThread: React.FC<CreateThreadProps> = ({
  isDirectMessage = false,
}) => {
  const translations = useTranslate({
    newThread: 'forum.new-thread',

    title: 'common.title',
    sticky: 'forum.sticky-thread',
    notification: ['forum.send-sms-label', { employeeNum: 0 }],
    email: 'forum.send-email-label',
    content: 'common.content',

    createThread: 'forum.create-thread',

    validation: {
      required: 'common.required',
      mustHaveContent: 'forum.validation.thread-must-have-content',
    },
    toast: {
      createSuccess: 'forum.toasts.create-thread-success',
    },
    error: {
      create: 'forum.errors.create-thread',
    },
  })

  const navigate = useNavigate()

  const quillRef = useRef<ForumQuillEditorRef>(null)

  const company = useCompany()
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()

  const canSendEmail = hasPermissions(PERMISSIONS.forum.send.thread_email)
  const canSendSms = hasPermissions(PERMISSIONS.forum.send.thread_sms)

  const {
    formValues: form,
    formChangeHandler: handler,
    formErrors: errors,
    updateForm,
    submitHandler: submit,
  } = useForm<CreateThreadForm>({
    values: {
      title: '',
      sticky: false,
      user: '',
      users: [],
      leaderGroups: [],
      sendSms: false,
      sendEmail: false,
    },
    validators: {
      title: val => (val ? null : translations.validation.required),
      ...(isDirectMessage && {
        users: (val, formValues) => {
          const hasUsers = val && val.length > 0
          const hasLeaderGroups =
            formValues?.leaderGroups && formValues.leaderGroups.length > 0

          return hasUsers || hasLeaderGroups
            ? null
            : translations.validation.required
        },
        leaderGroups: (val, formValues) => {
          const hasLeaderGroups = val && val.length > 0
          const hasUsers = formValues?.users && formValues.users.length > 0

          return hasLeaderGroups || hasUsers
            ? null
            : translations.validation.required
        },
      }),
    },
  })

  const isFormValid = useMemo(() => {
    const isTitleValid = !!form.title && form.title.trim().length > 0

    if (isDirectMessage) {
      const hasUsers = form.users && form.users.length > 0
      const hasLeaderGroups = form.leaderGroups && form.leaderGroups.length > 0
      const hasRecipients = hasUsers || hasLeaderGroups

      return isTitleValid && hasRecipients
    }
    return isTitleValid
  }, [form.title, form.users, form.leaderGroups, isDirectMessage])

  const selectAllOnChange: SelectMultipleOnSelectAll = (
    options,
    setSelectAll
  ) => {
    return (value: boolean) => {
      if (value) {
        updateForm({ users: options.map(option => option.value) as string[] })
        setSelectAll(true)
      } else {
        updateForm({ users: [] })
        setSelectAll(false)
      }
    }
  }

  const [createThread, { loading: createLoading }] = useMutation<
    CreateThreadMutation,
    CreateThreadMutationVariables
  >(CREATE_THREAD_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FORUM_THREADS_QUERY,
        variables: {
          first: DEFAULT_PAGE_SIZE,
          q: '',
        },
      },
    ],
    onCompleted(data) {
      if (!data?.createThread.ok) {
        addToast('error', translations.error.create)
        return
      }

      addToast('success', translations.toast.createSuccess)
      void navigate(`/forum/${data.createThread.thread.id}`)
    },
    onError: onErrorAuto(translations.error.create),
  })

  function handleSubmit(content: string, attachments: FileAttachment[]) {
    if (!quillRef.current?.quill) return () => void 0

    const text = quillRef.current.quill.getText()
    if (typeof text === 'undefined' || text.trim() === '') {
      addToast('warning', translations.validation.mustHaveContent)
      return () => void 0
    }

    return (values: CreateThreadForm) => {
      void createThread({
        variables: {
          ...values,
          leaderGroups: values.leaderGroups,
          content,
          attachments: attachments.map(attachment => attachment.file),
        },
      })
    }
  }

  const selectAllLeaderGroupsOnChange: SelectMultipleOnSelectAll = (
    options,
    setSelectAll
  ) => {
    return (value: boolean) => {
      if (value) {
        updateForm({
          leaderGroups: options.map(option => option.value) as string[],
        })
        setSelectAll(true)
      } else {
        updateForm({ leaderGroups: [] })
        setSelectAll(false)
      }
    }
  }

  async function handlePostSubmit(
    content: string,
    attachments: FileAttachment[]
  ) {
    submit(handleSubmit(content, attachments))()
  }

  return (
    <Wrapper>
      <header>
        <h1 className="text-4xl font-bold">
          {translations.newThread}

          {createLoading && (
            <div className="loader">
              <Loader.Spinner size={32} />
            </div>
          )}
        </h1>
      </header>

      {isDirectMessage && (
        <UserSelectField area="user">
          <SelectorsRow>
            <SelectMultipleUsersWrapper>
              <SelectMultipleUsers
                selected={form.users ?? []}
                grayed={false}
                width="100%"
                minWidth={isMobile ? '0' : '260px'}
                error={errors.users}
                onSelectAll={selectAllOnChange}
                filterLeaderGroup
                isDirect
                onChange={users => updateForm({ users })}
              />
            </SelectMultipleUsersWrapper>

            <SelectMultipleUsersWrapper>
              <SelectMultipleLeaderGroups
                selected={form.leaderGroups ?? []}
                grayed={false}
                width="100%"
                minWidth={isMobile ? '0' : '260px'}
                error={errors.leaderGroups}
                onSelectAll={selectAllLeaderGroupsOnChange}
                isDirect
                onChange={leaderGroups => updateForm({ leaderGroups })}
              />
            </SelectMultipleUsersWrapper>
          </SelectorsRow>
        </UserSelectField>
      )}

      <Form>
        <TitleSection area={'title'}>
          <div className="sticky">
            <Icon
              icon="thumbtack"
              size="1.6rem"
              type={form.sticky ? 'solid' : 'light'}
              color={form.sticky ? 'orange' : 'gray4'}
              hoverColor="orange"
              cursor="pointer"
              title={translations.sticky}
              onClick={() => updateForm({ sticky: !form.sticky })}
            />
          </div>

          <TitleInput
            value={form.title}
            error={errors.title}
            errorOffsetY="1.4rem"
            errorOffsetX="0.7rem"
            placeholder={translations.title}
            onChange={handler('title')}
          />
        </TitleSection>

        <ChoicesSection area={'choices'}>
          {canSendSms && (
            <Checkbox
              checked={form.sendSms}
              label={translations.notification({
                employeeNum: isDirectMessage
                  ? form.users.length + form.leaderGroups.length
                  : company.numberOfUsers,
              })}
              labelPosition={isMobileOnly ? 'right' : 'left'}
              boxSize={isMobileOnly ? '2rem' : '1.6rem'}
              onChange={handler('sendSms')}
            />
          )}
          {canSendEmail && (
            <Checkbox
              checked={form.sendEmail}
              label={translations.email}
              labelPosition={isMobileOnly ? 'right' : 'left'}
              boxSize={isMobileOnly ? '2rem' : '1.6rem'}
              onChange={handler('sendEmail')}
            />
          )}
        </ChoicesSection>

        <ContentSection area={'content'}>
          <ForumQuillEditor
            ref={quillRef}
            placeholder={translations.content}
            minHeight="8rem"
            submitText={translations.createThread}
            noAttachmentReset
            isExternallyValid={isFormValid}
            onSubmit={handlePostSubmit}
          />
        </ContentSection>
      </Form>
    </Wrapper>
  )
}
